import React from 'react'
import { Alert, Divider, Stack, Typography } from '@mui/material'
import DocLink from '../../components/DocLink'
import { Screenshot } from '../../components/DocsComponents'

const Organisations = () => (
  <Stack gap={3} divider={<Divider />}>
    <Stack gap={1}>
      <Typography variant="title3">What organisations are for</Typography>
      <Typography>
        PTS organisations represent hospitals, wards, departments, or other
        requesters involved in patient transport bookings. They are separate
        from event clients and are used inside the PTS booking workflow.
      </Typography>
      <Typography>
        Open <DocLink url="/pts/organisations" text="PTS Organisations" />.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">The Organisations Hub</Typography>
      <Typography>
        <DocLink url="/pts/organisations" text="PTS Organisations" /> is a
        dashboard, not a plain list. Each organisation is shown as a tile with
        a booking-volume sparkline, an outstanding-invoice figure, a count of
        hospital portal users, and a set of adoption chips — for example
        whether any portal user has logged in recently, and whether the
        organisation has been sent the "your hospital has portal access"
        announcement yet.
      </Typography>
      <Typography>
        The top of the page summarises the whole book of business —
        organisations, journeys this month, portal-booking share, outstanding
        invoice value, active portal users, and journeys still to plan — and
        the tiles can be sorted by most active, name, outstanding balance, or
        journeys to plan.
      </Typography>
      <Typography>
        Use the tile actions to <strong>Open</strong> the organisation's full
        record, start a <strong>New Booking</strong> for it,{' '}
        <strong>resend an invite</strong> to a portal user who hasn't set
        their password, or <strong>announce the portal</strong> to an
        organisation that has portal access configured but doesn't seem to be
        using it — this sends an email explaining what the hospital booking
        portal is and how to log in.
      </Typography>
      <Typography>
        <strong>Portal Preview</strong> opens that organisation's hospital
        portal exactly as its users would see it, with a switcher for Ward,
        Site, or Admin view — the quickest way to check what a hospital user
        is looking at before talking them through it.
      </Typography>
      <Alert severity="info" variant="outlined">
        This dashboard exists because organisations frequently don't realise
        their hospital already has portal access. If a hospital keeps phoning
        or emailing bookings in instead of using the portal, check their tile
        here first.
      </Alert>
      <Screenshot url="patient-transport/organisations-hub.png">
        The Organisations Hub showing a grid of organisation tiles with
        sparklines, outstanding-invoice figures, portal user counts, and
        adoption chips.
      </Screenshot>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Adding organisations</Typography>
      <Typography>
        Add a PTS organisation before creating hospital-linked bookings or
        portal users. The organisation record stores the name, address,
        contacts, ward and department structure, and portal access for external
        hospital users.
      </Typography>
      <Typography>
        Organisation addresses are used by the booking workflow when a hospital
        pickup or dropoff is selected, so keep address details accurate enough
        for route planning and mapping.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Wards and departments</Typography>
      <Typography>
        Organisation records can include ward and department information. During
        booking creation, selecting an organisation can make related requester
        and ward details available.
      </Typography>
      <Typography>
        Ward and department records are used as booking context and can also act
        as the hospital address source for pickup or dropoff locations. Keep
        names, internal locations, and active status current so portal users and
        coordinators see the right options.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Requesters</Typography>
      <Typography>
        The booking form records who requested the transport. Requesters can be
        organisation contacts or internal users depending on the booking source
        and selected organisation.
      </Typography>
      <Typography>
        Portal users are also available in the Requested By list. Ward users are
        filtered to the selected ward. Site and Admin users are available across
        the organisation.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Hospital portal users</Typography>
      <Typography>
        Organisation records can be used to create and maintain hospital booking
        portal users. Add the user's name and email address, choose Ward, Site,
        or Admin access, and assign permitted wards for Ward access users.
      </Typography>
      <Typography>
        New portal users receive a set-password email. They must complete this
        step before they can log in to the hospital booking portal.
      </Typography>
      <ul style={{ marginTop: 0, lineHeight: 2 }}>
        <li>
          <strong>Ward</strong> access should always include at least one active
          ward or department.
        </li>
        <li>
          <strong>Site</strong> access is used for organisation-level booking
          and journey visibility.
        </li>
        <li>
          <strong>Admin</strong> access is used for the broadest portal
          oversight for that organisation.
        </li>
      </ul>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Contract, tariff & SLA targets</Typography>
      <Typography>
        Using <strong>Open</strong> on a hub tile takes you to the
        organisation's full client record, which for PTS organisations shows
        a <strong>PTS Contract &amp; Tariff</strong> section. This is where
        you set:
      </Typography>
      <ul style={{ marginTop: 0, lineHeight: 2 }}>
        <li>
          Which <strong>tariff</strong> (rate card) prices this organisation's
          work — leave blank to use the tenant's default tariff.
        </li>
        <li>
          <strong>Invoice grouping</strong> — one invoice per journey, per
          booking, per patient (the default for a new organisation), or
          consolidated weekly/monthly across the whole organisation. This is
          what a billing run uses to decide how to bundle a period's
          completed journeys. An organisation with no contract set up yet
          still uses the per-patient default.
        </li>
        <li>
          <strong>PO number / payment terms</strong>, and whether a{' '}
          <strong>quote is required</strong> before a booking can move into
          planning.
        </li>
        <li>
          <strong>SLA targets</strong> — optional minimum on-time pickup %,
          minimum on-time arrival %, maximum aborted %, and maximum no-show %
          for this organisation. Set any of these to have the performance
          pack below show a clear met/missed result against them; leave a
          target blank to skip it.
        </li>
      </ul>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">SLA &amp; performance</Typography>
      <Typography>
        The same organisation record also has an{' '}
        <strong>SLA &amp; Performance</strong> section: on-time pickup %,
        on-time arrival %, aborted/no-show counts, and portal-booking share
        for a chosen date range, each shown against the SLA targets above
        where one is set. Use <strong>Download PDF</strong> to generate a
        performance pack for a contract review meeting — it's a client-side
        export, so nothing needs to be pre-generated or emailed.
      </Typography>
      <Alert severity="info" variant="outlined">
        These figures use the same on-time definitions as{' '}
        <DocLink url="/patient-transport/reports" text="PTS Reports" docsPage />
        , just scoped and compared to one organisation's targets.
      </Alert>
      <Screenshot url="patient-transport/organisation-sla.png">
        The SLA &amp; Performance section on an organisation's profile,
        showing on-time pickup/arrival tiles with met/missed chips and the
        Download PDF button.
      </Screenshot>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Portal-created bookings</Typography>
      <Typography>
        Where a patient or organisation submits a booking through a portal, the
        requester and organisation details may be pre-populated and locked to
        preserve the submitted request information.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Troubleshooting access</Typography>
      <Typography>
        If a hospital user can log in but cannot see journeys, check that they
        are linked to the correct organisation, their account can log in, their
        email setup is complete, and their access level matches the intended
        scope. For Ward users, also confirm they have at least one active ward
        assigned.
      </Typography>
    </Stack>
  </Stack>
)

export default Organisations
